window.theme = window.theme || {};
window.theme.utils = {
  moneyFormat(cents, format) {
    return this.format(cents, format);
  },
  moneyFormatWithCurrency(cents, format) {
    return this.format(cents, format, true);
  },
  format(cents, format, currency = false) {
    const moneyFormat = (currency ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat) || "${{amount}}";

    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }
    let value = "";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || moneyFormat;

    function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
      if (isNaN(number) || number == null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split(".");
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const centsAmount = parts[1] ? decimal + parts[1] : "";

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case "amount":
        value = formatWithDelimiters(cents, 2);
        break;
      case "amount_no_decimals":
        value = formatWithDelimiters(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = formatWithDelimiters(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = formatWithDelimiters(cents, 0, ".", ",");
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute("form", `${element.form.getAttribute("id")}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"));

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute("aria-expanded", !event.currentTarget.closest("details").hasAttribute("open"));
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus?.focus();

  if (elementToFocus.tagName === "INPUT" && ["search", "text", "email", "url"].includes(elementToFocus.type) && elementToFocus.value) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + "pauseVideo" + '","args":""}', "*");
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === "minus") {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle("disabled", parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener ? target.addEventListener(eventName, callback, false) : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options["hideElement"] || province_domid);

  Shopify.addListener(this.countryEl, "change", Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) => summary.addEventListener("click", this.onSummaryClick.bind(this)));
    this.querySelectorAll("button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)").forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector("button"));
      summaryElement.nextElementSibling.removeEventListener("transitionend", addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches ? addTrapFocus() : summaryElement.nextElementSibling.addEventListener("transitionend", addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((submenu) => {
      submenu.classList.remove("submenu-open");
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(detailsElement.closest("details[open]"), detailsElement.querySelector("summary"));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.stickyHeader = this.closest("sticky-header");
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0;
    document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    if (this.stickyHeader) {
      this.stickyHeader.classList.add("header-menu-open");
    }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);

    if (this.stickyHeader) {
      this.stickyHeader.classList.remove("header-menu-open");
    }
  }

  onResize = () => {
    this.header && document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`);
    document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`);
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, false));
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (event.pointerType === "mouse" && !event.target.closest("deferred-media, product-model")) this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace("shopify-section-", "");
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, "text/html");
            const sourceQty = html.querySelector(".quick-order-list-container").parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`));
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(true));

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(content.querySelector("video, model-viewer, iframe"));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == "VIDEO" && deferredElement.getAttribute("autoplay")) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition = event.currentTarget.name === "next" ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener("click", this.linkToSlide.bind(this)));
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active");
    this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === "next" && !isLastSlide) || (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target.closest(".product-form__input").querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", swatchValue);
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty("--swatch--background", "unset");
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty("--swatch-focal-point", target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset");
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll("select option[selected], fieldset input:checked")).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener("storefront:signincompleted", this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace("[min]", event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace("[max]", event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace("[step]", event.target.step));
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }
}

/* 
  Demo:
  <common-swiper>
    <script type="application/json" data-swiper-config>
      {
        "slidesPerView": 1.4,
        "spaceBetween": 16,
        "ResizeObserverSize": true,
        "breakpoints": {
          "768": {
            "slidesPerView": 2.4,
            "spaceBetween": 20
          }
        }
      }
    </script>
    <div class="swiper">
      <div class="swiper-wrapper">
        <div class="swiper-slide"></div>
        <div class="swiper-slide"></div>
      </div>
    </div>
  </common-swiper> 
*/
customElements.define(
  "common-swiper",
  class CommonSwiper extends HTMLElement {
    // 函数返回避免数据共享
    static config = () => ({
      slidesPerView: 1,
      spaceBetween: 10,
      breakpoints: {
        768: {
          spaceBetween: 24,
        },
        1240: {
          spaceBetween: 32,
        },
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
    });

    constructor() {
      super();
    }
    connectedCallback() {
      this.container = this.querySelector(".swiper");
      if (!this.container) return;
      if (!this.querySelectorAll(".swiper-slide")?.length) return;
      this.initSwiper();
    }
    initSwiper() {
      this.config = this.getConfig();
      this.swiper = new Swiper(this.container, this.config);
    }

    getConfig() {
      let config = JSON.parse(this.querySelector("script[data-swiper-config]")?.textContent || null) || null;
      config = config || CommonSwiper.config();
      if (config.navigation) {
        // 判断config.navigation.nextEl是dom对象还是字符串
        if (typeof config.navigation.nextEl === "string") {
          config.navigation.nextEl = this.querySelector(config.navigation.nextEl);
        }
        if (typeof config.navigation.prevEl === "string") {
          config.navigation.prevEl = this.querySelector(config.navigation.prevEl);
        }
      }
      if (this.querySelector(".swiper-pagination")) {
        config.pagination = {
          el: this.querySelector(".swiper-pagination"),
          clickable: true,
        };
      } else {
        delete config.pagination;
      }
      return config;
    }
  }
);

customElements.define(
  "scroll-view",
  class extends HTMLElement {
    constructor() {
      super();
      this.city = this.dataset.city;
    }

    connectedCallback() {
      this.form = this.querySelector("form");

      this.updateTotalPrice();
      this.form.addEventListener("change", () => {
        this.updateTotalPrice();
      });

      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.fetchResult();
      });

      this.handles = this.querySelectorAll("anchor-point");
      if (!this.handles) return;

      this.isMobile = window.innerWidth < 768;
      this.overHeight = 0;
      this.customOffset = 50; // 手动调整中心线的偏移量

      if (this.isMobile) {
        this.customOffset = this.customOffset / 2;
        this.overHeight = this.querySelector(".details-list").getBoundingClientRect().height;
        this.querySelector(".details-content").style.height = this.querySelector(".details-content").getBoundingClientRect().height + "px";
        window.addEventListener("scroll", this.onScroll.bind(this));
      } else {
        this.initFooterEvent();
      }

      const observer = new IntersectionObserver(
        () => {
          // const coors = entries.map(entry => ({ target: entry.target, y: entry.intersectionRect.y })); // 废弃，缺陷：若元素完全出现在屏幕内时threshold持续为1将无法被监听， 锚点扫描将计算错误
          // 扫描所有的锚点，找到最接近屏幕中心的锚点
          const coors = this.getHandlesCoors();
          const { target: winner } = this.radar(coors, (window.innerHeight - this.overHeight) / 2 + this.overHeight - this.customOffset);
          this.updateScreen(winner);
        },
        {
          threshold: new Array(10).fill(0).map((_, i) => ++i / 10),
        }
      );
      this.handles.forEach((handle) => observer.observe(handle));

      document.addEventListener("show:view", () => {
        this.show();
      });
    }

    initFooterEvent() {
      const footer = this.querySelector(".details-footer");
      footer.style.height = this.querySelector(".details-footer-wrapper").getBoundingClientRect().height + "px";
      const contact = document.querySelector(".order-result .contact");
      contact.style.height = document.querySelector(".order-result .contact-wrapper").getBoundingClientRect().height + "px";

      const onScrollForFooter = function () {
        const footerBCR = footer.getBoundingClientRect();
        if (footerBCR.top >= window.innerHeight - footerBCR.height - 30) {
          footer.setAttribute("fixed", "");
        } else {
          footer.removeAttribute("fixed");
        }

        const contactBCR = contact.getBoundingClientRect();
        if (contactBCR.top >= window.innerHeight - contactBCR.height - 30) {
          contact.setAttribute("fixed", "");
        } else {
          contact.removeAttribute("fixed");
        }
      };
      onScrollForFooter();
      window.addEventListener("scroll", onScrollForFooter);
    }

    onScroll() {
      const top = this.querySelector(".details-inner").getBoundingClientRect().top;
      if (top <= 0) {
        this.classList.add("scroll-overlay");
      } else {
        this.classList.remove("scroll-overlay");
      }
    }

    getHandlesCoors() {
      return Array.from(this.handles).map((handle) => {
        const { y, height } = handle.getBoundingClientRect();
        return { target: handle, y: y + height / 2 };
      });
    }

    radar(coors, centerY) {
      const sorted = coors.sort((a, b) => Math.abs(a.y - centerY) - Math.abs(b.y - centerY));
      const closest = sorted[0];
      closest.target.classList.add("active");
      sorted.slice(1).forEach((coor) => {
        coor.target.classList.remove("active");
      });
      return closest;
    }

    updateScreen(target) {
      document.dispatchEvent(
        new CustomEvent("screen:update", {
          detail: {
            id: target.getAttribute("for"),
            checked: target.getChecked(),
          },
        })
      );
    }

    updateTotalPrice() {
      let cost = 0,
        discount_cost = 0;
      this.chooses = this.querySelectorAll(".option-input:checked");
      const area = Number(this.querySelector('[name="option-product"]:checked').closest(".value-item").querySelector(".option-area")?.value || 1);
      this.chooses.forEach((choose) => {
        let choose_cost = Number(choose.closest(".value-item").querySelector(".option-cost").value);
        let choose_discount_cost = choose.closest(".value-item").querySelector(".option-discount-cost")
          ? Number(choose.closest(".value-item").querySelector(".option-discount-cost").value)
          : choose_cost;

        if (choose.hasAttribute("unit-cost")) {
          choose_cost = choose_cost * area;
          choose_discount_cost = choose_discount_cost * area;
        }

        cost += choose_cost;
        discount_cost += choose_discount_cost;
      });
      if (discount_cost < cost) {
        this.querySelector("[data-total-cost]").innerHTML = `
        <span>${theme.utils.moneyFormat(discount_cost).replace(".00", "")}</span>
        <span class="original">${theme.utils.moneyFormat(cost).replace(".00", "")}</span>
      `;
      } else {
        this.querySelector("[data-total-cost]").innerHTML = `<span>${theme.utils.moneyFormat(cost).replace(".00", "")}</span>`;
      }
    }

    fetchResult() {
      this.hide();
      document.dispatchEvent(
        new CustomEvent("order:success", {
          detail: this.getOrderData(),
        })
      );
    }

    getOrderData() {
      const result = [];
      this.handles.forEach((item) => {
        const current = item.getResult();
        if (current) {
          result.push(current);
        }
      });
      return {
        options: result,
        total: this.querySelector("[data-total-cost]").innerHTML,
        cost: Array.from(this.querySelectorAll("[data-total-cost] span"))
          .map((e) => e.textContent)
          .join("/"),
        image: this.querySelector("#anchor-product .option-image[data-area='show']").innerHTML,
      };
    }

    hide() {
      this.classList.add("hidden");
    }

    show() {
      this.classList.remove("hidden");
    }
  }
);

customElements.define(
  "anchor-point",
  class extends HTMLElement {
    constructor() {
      super();

      this.isModel = this.dataset.model === "true";
      this.is_checkbox = this.dataset.type === "checkbox";

      if (!this.isModel) {
        document.addEventListener("model:change", (event) => {
          event.detail?.handle && this.updatePriceByModel(event.detail.handle);
        });
      }
    }

    updatePriceByModel(handle) {
      this.querySelectorAll(".value-item").forEach((item) => {
        if (item.querySelector(".value-price")) this.updateItemPrice(item, handle);
      });
    }

    updateItemPrice(item, handle) {
      const key = item.querySelector(".option-input").dataset.handle;
      if (!this.data[key].model_prices?.length) return;
      const price = this.getPriceByHandle(key, handle);

      item.querySelector(".value-price").innerHTML = `<span>+ ${theme.utils.moneyFormat(price).replace(".00", "")}</span>`;
      item.querySelector(".option-cost").value = price;
    }

    getPriceByHandle(key, handle) {
      return this.data[key].model_prices.find((value) => value.handle === handle)?.price || this.data[key].price;
    }

    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[type="application/ld+json"]')?.textContent || null);
      Object.keys(this.data).forEach((key, i) => {
        this.data[key].standard = this.querySelector(".standard").innerHTML;
      });
      const inputs = this.querySelectorAll('input[type="radio"], input[type="checkbox"]');
      this.checked = this.querySelectorAll("input:checked");
      this.lastInput = this.checked[this.checked?.length - 1] || inputs[0];
      // 获取所有radio类型的input，监听单选框选中事件
      inputs.forEach((input) => {
        input.addEventListener("change", () => {
          this.checked = this.querySelectorAll("input:checked");
          this.lastInput = input.checked ? input : this.checked[this.checked?.length - 1] || inputs[0];

          document.dispatchEvent(
            new CustomEvent("option:change", {
              detail: {
                optionId: this.getAttribute("for"),
                value: this.lastInput.dataset.handle,
              },
            })
          );
          if (this.isModel) {
            const handle = this.lastInput.dataset.handle;
            // 替换链接中的handle参数
            const url = new URL(window.location.href);
            url.searchParams.set("handle", handle);
            window.history.replaceState({}, "", url.toString());
            document.dispatchEvent(
              new CustomEvent("model:change", {
                detail: {
                  handle: handle,
                },
              })
            );
          }
        });
      });
    }

    getResult() {
      if (this.checked?.length) {
        return {
          option: this.data[this.checked[0].dataset.handle].option,
          standard: this.data[this.checked[0].dataset.handle].standard,
          values: Array.from(this.checked).map((item) => {
            return this.data[item.dataset.handle];
          }),
        };
      }
      return null;
    }

    getChecked() {
      const { id, name } = this.querySelector("input:checked") || this.querySelector("input.option-input");
      return { id, name };
    }
  }
);

customElements.define(
  "anchor-screen",
  class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.areas = this.querySelectorAll("anchor-area");
      this.areaMap = new Map();
      this.areas.forEach((area) => {
        this.areaMap.set(area.id, area);
      });

      document.addEventListener("screen:update", (event) => {
        event.detail.id && this.showScreenById(event.detail.id);
      });

      document.addEventListener("option:change", (event) => {
        const { optionId, value } = event.detail;
        this.areaMap.get(optionId).showValue(value);
      });
    }

    showScreenById(id) {
      this.areas.forEach((area) => {
        if (area.id === id) {
          area.setAttribute("visible", "");
        } else {
          area.removeAttribute("visible");
        }
      });
    }
  }
);

customElements.define(
  "anchor-area",
  class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.images = this.querySelectorAll("[data-area]");
    }

    showValue(value) {
      this.images.forEach((item) => {
        item.setAttribute("data-area", item.getAttribute("from") === value ? "show" : "");
      });
    }
  }
);

customElements.define(
  "search-iframe",
  class extends HTMLElement {
    constructor() {
      super();

      this.search_result = this.querySelector(".search-result-inner");
      this.search_result?.addEventListener("click", (event) => {
        if (event.target.tagName === "LI") {
          this.fetchLocation(event.target.dataset.q);
        }
      });
      window.addEventListener("message", (event) => {
        // 解析消息内容
        const data = event.data;
        if (data.type === "redirect" && data.params) {
          //监听跳转
          if (this.selectList?.length) {
            this.querySelector(".search-list-item").click();
          } else {
            const params = JSON.parse(data.params);
            //获取经纬度和keyword参数，然后主系统带上这些参数，跳转到自己的页面地址。嵌入的iframe带上这些参数，例如：https://aduplus.web.app/summary?q=969+Franquette+Ave,+San+Jose,+CA+95125%E7%BE%8E%E5%9B%BD&latitude=37.2921363&longitude=-121.885206
            console.log("redirect params", params);
            if (params.noData) return;
            if (params.city) {
              localStorage.setItem("city", params.city);
            }
            window.location.href = `/search?q=${params.keyword}***latitude=${params.location.latitude}***longitude=${params.location.longitude}&city=${params.city}`;
          }
        } else if (data?.type === "getCity" && data?.params) {
          const params = JSON.parse(data.params); //可以获取经纬度和keyword参数,keyword就是输入的city。
          console.log("getCity params:", params);
          if (params.noData) {
            document.querySelector(".result-container").classList.add("no-data");
          } else {
            document.querySelector(".result-container").classList.remove("no-data");
          }
          if (params.city) {
            localStorage.setItem("city", params.city);
            console.log(`/search?q=${params.keyword}***latitude=${params.location.latitude}***longitude=${params.location.longitude}&city=${params.city}`);
            // window.location.href = `/search?q=${params.keyword}***latitude=${params.location.latitude}***longitude=${params.location.longitude}&city=${params.city}`;
          }
          this.filterModel(params.maxSize);
        } else if (data?.type === "getList" && data?.params) {
          const params = JSON.parse(data.params); //可以获取经纬度和keyword参数,keyword就是输入的city。
          console.log("getList params:", params);
          this.selectList = params;
          if (this.search_result) this.renderList(params);
        }
      });
    }

    filterModel(maxSize) {
      const models = document.querySelectorAll(".model-option");
      if (maxSize > 0) {
        models.forEach((model) => {
          if (Number(model.dataset.area) > maxSize) {
            model.querySelector("input").setAttribute("disabled", "");
          } else {
            model.querySelector("input").removeAttribute("disabled");
          }
        });
      } else {
        models.forEach((model) => {
          model.querySelector("input").removeAttribute("disabled");
        });
      }
    }

    renderList(list) {
      if (list?.length) {
        const listHtml = list
          .map((item) => {
            return `<li class="search-list-item" data-q="${item.description}">${item.description}</li>`;
          })
          .join("");
        this.querySelector(".search-result-inner").innerHTML = `<ul class="search-list list-unstyled">${listHtml}</ul>`;
      } else {
        this.querySelector(".search-result-inner").innerHTML = "";
      }
    }

    fetchLocation(address) {
      fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${address}&key=${theme.token}&language=en`)
        .then((response) => response.json())
        .then((data) => {
          console.log("fetchLocation data:", data);
          if (!data.results.length) return;
          const result = data.results[0];
          const cityComponent = result.address_components.find((comp) => comp.types.includes("locality"));
          console.log("City:", cityComponent.long_name);
          localStorage.setItem("city", cityComponent.long_name);
          const { lat, lng } = result.geometry.location;
          window.location.href = `/search?q=${address}***latitude=${lat}***longitude=${lng}&city=${cityComponent.long_name}`;
        });
    }
  }
);

customElements.define(
  "order-result",
  class extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.querySelector("[aria-back]").addEventListener("click", () => {
        this.hide();
        this.result?.remove();
        document.dispatchEvent(new CustomEvent("show:view"));
      });

      document.addEventListener("order:success", (event) => {
        this.renderResult(event.detail);
      });
    }

    renderResult(data) {
      this.show();
      // 渲染前跳到页面顶部
      window.scrollTo(0, 0);
      this.result = this.parseResultHtml(data);
      // 往前插入
      this.insertBefore(this.result, this.firstChild);
    }

    parseResultHtml(data) {
      const product = data.options.splice(0, 1)[0]?.values[0];
      const html = `
      <div class="order-result-wrapper">
        <div class="result-summary">
          <div class="result-header">
            <div class="result-header-block">
              <p>${product.name}</p>
              <h2>Order Detail</h2>
            </div>
            <div class="result-header-block">
              <p>Total Amount</p>
              <h2>${data.total}</h2>
            </div>
          </div>
        </div>
        <div class="result-details">
          <div class="result-image">${data.image}</div>
          <div class="result-info">
            <div>
              <h3>APEX | ${product["collection-title"]}</h3>
              <p>${product.name}</p>
            </div>
            <div class="medium:hide order-total">
              <p>Total Amount</p>
              <h3>${data.total}</h3>
            </div>
          </div>
          <div class="result-option">${data.options
            .map((item) => {
              return `<div class="result-option-item">
              <h4>${item.option}</h4>
              <div class="result-block">
                <h5>Standard</h5>
                <div class="result-standard">${item.standard}</div>
              </div>
              <div class="result-block">
                <h5>Upgrades</h5>
                <div class="result-upgrades">
                  <h6>Option</h6>
                  <div class="result-upgrades-list">
                    ${item.values
                      .map((value) => {
                        return `
                            <div class="result-upgrades-content">${
                              value.money
                                ? `
                                <p class="name">${value.name}</p>
                                <p>${value.description}</p>
                                <p>${value.money.replace(".00", "")}</p>
                              `
                                : "None"
                            }</div>
                        `;
                      })
                      .join("")}
                  </div>
                </div>
              </div>
            </div>`;
            })
            .join("")}</div>
        </div>
      </div>
    `;
      // 解析dom字符串
      const doc = new DOMParser().parseFromString(html, "text/html");
      return doc.body.firstChild;
    }

    show() {
      this.classList.remove("hidden");
      setTimeout(() => {
        this.querySelector(".contact").style.height = this.querySelector(".contact-wrapper").getBoundingClientRect().height + "px";
      }, 16);
    }
    hide() {
      this.classList.add("hidden");
    }
  }
);

class Toast {
  static colors = {
    success: "#1afa29",
    error: "#d81e06",
  };
  constructor({ type, content } = { type: "success", content: "this is a toast" }) {
    this.type = type;
    this.content = content;
    this.init();
  }
  init() {
    const toast = document.createElement("div");
    toast.classList.add("toast");
    toast.classList.add(this.type);
    toast.style = `
      position: fixed;
      z-index: 99999999;
      top: 20px;
      left: 50%;
      z-index: 9999;
      background-color: #fff;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
      padding: 16px;
      max-width: 560px;
      width: 90%;
      box-sizing: border-box;
      opacity: 0;
      font-size: 14px;
      transform: translate(-50%, -30px);
      transition: opacity 0.3s ease, transform 0.3s ease;
    `;
    toast.innerHTML = `
      <div style="display: flex;align-items: center;justify-content: center;gap: 10px;">
        <div style="font-size: 0px;">
          <svg viewBox="0 0 1024 1024" style="color: ${
            this.type == "success" ? Toast.colors.success : Toast.colors.error
          }" version="1.1" width="30" height="30"><path d="M887.466667 512a375.466667 375.466667 0 1 0-750.933334 0 375.466667 375.466667 0 0 0 750.933334 0z m51.2 0c0 235.648-191.018667 426.666667-426.666667 426.666667S85.333333 747.648 85.333333 512 276.352 85.333333 512 85.333333s426.666667 191.018667 426.666667 426.666667zM512 298.496a25.6 25.6 0 0 1 25.6 25.6v261.674667a25.6 25.6 0 1 1-51.2 0V324.096a25.6 25.6 0 0 1 25.6-25.6z m0 354.474667a25.6 25.6 0 0 1 25.6 25.6v21.333333a25.6 25.6 0 1 1-51.2 0v-21.333333a25.6 25.6 0 0 1 25.6-25.6z" fill="currentColor"></path></svg>
        </div>
        <div class="toast-message">${this.content}</div>
      </div>
    `;
    this.toast = toast;
    document.body.appendChild(toast);
    this.show();
  }

  show() {
    this.toast.style.display = "block";
    this.toast.style.opacity = 1;
    this.toast.style.transform = "translate(-50%, 0)";
    setTimeout(() => {
      this.hide();
    }, 4000);
  }

  hide() {
    this.toast.style.display = "none";
    this.toast.style.opacity = 0;
    this.toast.style.transform = "translate(-50%, -30px)";
  }
}

customElements.define(
  "contact-me",
  class extends HTMLElement {
    constructor() {
      super();

      this.requiredFields = this.dataset.requiredFields.slice(0, -1).split(",");

      this.isUILoaded = false;
      setTimeout(() => {
        this.loadElementUI();
      }, 160);
    }

    connectedCallback() {
      this.querySelector(".contact-close").addEventListener("click", () => {
        this.hide();
      });
      this.querySelector(".overlay").addEventListener("click", () => {
        this.hide();
      });
      document.addEventListener("show:contact", (event) => {
        this.orderData = event.detail;
        if (this.orderData?.options?.length > 1) {
          const product = this.orderData.options.splice(0, 1)[0]?.values[0];
          console.log(product);
          if (product && this.querySelector(`input[value='${product.handle}']`)) {
            this.querySelector(`input[value='${product.handle}']`).checked = true;
          }
          this.orderInfo = this.orderData.options.map((item) => ({ option: item.option, name: item.values.map((e) => e.name).join("/") }));
        }
        this.show();
      });

      this.form = this.querySelector(".contact-form");
      this.mcForm = this.querySelector("#mc_embed_signup");
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        if (this.validate() && this.termChecked()) this.submitForm();
      });
      this.form.querySelectorAll("input:not(.form-control)").forEach((input) => {
        input.addEventListener("focus", (event) => {
          event.target.nextElementSibling.innerText = "";
        });
      });
      this.initFormListen();

      this.initMcFormField();
    }

    initFormListen() {
      const _this = this;
      // 增加日志，确认是否正确获取到目标元素
      const targetNode = document.querySelector("#mce-success-response");
      const observer = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === "childNodes" || mutation.type === "childList") {
            _this.success();
          }
        }
      });

      // 修改配置以监听文本内容变化
      const config = {
        childList: true,
        characterData: true,
        subtree: true,
      };
      observer.observe(targetNode, config);
    }

    initMcFormField() {
      const names = ["FNAME", "LNAME", "EMAIL", "PHONE", "PADDRESS", "FLOORPLAN", "MEETING", "MESSAGE", "WINDOW", "INTERIOR", "KITCHEN", "BATHADD", "BATCABINET", "OTHER", "TIMELINE", "BUDGET"];
      this.fields = {};
      names.forEach((name) => {
        this.fields[name] = this.querySelector(`input[name="${name}"]`) || null;
      });
    }

    success() {
      this.hide();
      new Toast({ type: "success", content: "Thank you for your interest! We will get back to you soon." });
    }

    termChecked() {
      const term = this.querySelector(`#term-service`);
      const privacy = this.querySelector(`#privacy-policy`);
      if (!term.checked || !privacy.checked) {
        new Toast({
          type: "error",
          content: "Please review our [Privacy Policy] and [Term of Use] before submitting the form. By submitting, you confirm that you have read and agreed to the terms outlined.",
        });
        return false;
      }
      return true;
    }

    validate() {
      let valid = true;
      this.requiredFields.forEach((field) => {
        let input = this.form.querySelector(`input[name="${field}"]`);
        if (input.type !== "checkbox" && input.type !== "radio") {
          if (!input.value) {
            // 下一个元素是form__block-error
            input.nextElementSibling.innerText = "This field is required.";
            valid = false;
          } else {
            if (field === "EMAIL" && this.validateEmail(input.value)) {
              input.nextElementSibling.innerText = "Please enter a valid email address.";
              valid = false;
            } else {
              input.nextElementSibling.innerText = "";
            }
          }
        } else {
          input = this.form.querySelector(`input[name="${field}"]:checked`);
          if (!input) {
            this.form.querySelector(`input[name="${field}"]`).closest(".form__block").querySelector(".form__block-error").innerText = "This field is required.";
            valid = false;
          } else {
            input.closest(".form__block").querySelector(".form__block-error").innerText = "";
          }
        }
      });
      const email = this.form.querySelector(`input[name="EMAIL"]`);
      if (this.validateEmail(email.value)) {
        email.nextElementSibling.innerText = "Please enter a valid email address.";
        valid = false;
      }
      if (!valid) this.scrollFormTop();
      return valid;
    }

    validateEmail(email) {
      return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    submitForm() {
      const formData = new FormData(this.form);
      const data = {};
      formData.forEach((value, key) => {
        data[key] ? (data[key] += `,${value}`) : (data[key] = value);
      });
      this.createFormData(data);
    }

    createFormData(data) {
      // const names = ["FNAME", "LNAME", "EMAIL", "PHONE", "PADDRESS", "FLOORPLAN", "MEETING", "MESSAGE", "WINDOW", "INTERIOR", "KITCHEN", "BATHADD", "BATCABINET", "OTHER"];
      // data.ORDER_INFO = this.orderInfo;
      const nameMap = {
        Window: "WINDOW",
        Interior: "INTERIOR",
        Kitchen: "KITCHEN",
        Bathroom: "BATHADD",
        "Bathroom Cabinet": "BATCABINET",
      };
      data.OTHER = "";
      this.orderInfo?.forEach((item) => {
        if (nameMap[item.option]) {
          data[nameMap[item.option]] = item.name;
        } else {
          data.OTHER += `${item.option}: ${item.name};`;
        }
      });

      data.MEETING = `${data.meeting_start}-${data.meeting_end}`;
      delete data.meeting_start;
      delete data.meeting_end;
      Object.keys(data).forEach((key) => {
        if (this.fields[key]) this.fields[key].value = data[key];
      });
      this.postWebhook(data);
      this.mcForm.querySelector('[type="submit"]').click();
    }

    postWebhook(formData) {
      const url = "https://hook.us2.make.com/x8sn1i3nrj8b6dmd932kme7s8h4jrkb8";
      // {
      //   "first_name": "Tobey",
      //   "last_name": "L",
      //   "phone_number": "222-333-444",
      //   "email_address": "developer@apex.com",
      //   "property_address": "906 vine street",
      //   "prefered_floor_plan": "2B2B 749 SQFT",
      //   "schedule_1_1_meeting": "05/01/2025-05/05/2025",
      //   "messge": "I would like to know more about the 2B2B ADU",
      //   "window": "aluminum window frames",
      //   "interior_add_ons": "engineered flooring",
      //   "kitchen_cabinet": "european shaker-style",
      //   "bathroom_add_ons": "european shaker-style",
      //   "bathroom_caninet": "european shaker-style",
      //   "total_price": "$170,500",
      //   "Department/Brand": "Apex Homes",
      //   "Apex_Stages": "Inquiry",
      //   "RecordTypeId": "012Hs000000wtyX",
      //   "other": ""
      // }
      const data = {
        first_name: formData.FNAME || "",
        last_name: formData.LNAME || "",
        phone_number: formData.PHONE || "",
        email_address: formData.EMAIL || "",
        property_address: formData.PADDRESS || "",
        prefered_floor_plan: formData.FLOORPLAN || "",
        schedule_1_1_meeting: formData.MEETING || "",
        messge: formData.MESSAGE || "",
        budget: formData.BUDGET || "",
        timeline: formData.TIMELINE || "",
        window: formData.WINDOW || "",
        interior_add_ons: formData.INTERIOR || "",
        kitchen_cabinet: formData.KITCHEN || "",
        bathroom_add_ons: formData.BATHADD || "",
        bathroom_caninet: formData.BATCABINET || "",
        total_price: this.orderData?.cost || "",
        "Department/Brand": "Apex Homes",
        Apex_Stages: "Inquiry",
        RecordTypeId: "012Hs000000wtyX",
        other: formData.OTHER || "",
      };
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("Webhook 发送成功:", result);
        })
        .catch((error) => {
          console.error("Webhook 发送失败:", error);
        });
    }

    scrollFormTop() {
      if (!this.inner) this.inner = this.querySelector(".contact-inner");
      this.inner.scrollTo(0, 0);
    }

    loadElementUI() {
      if (this.isUILoaded) return;
      // const assets = {
      //   css: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/elementui.css?v=81714655923162961401744852013",
      //   vue: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/vue.js?v=129580119698169269411744851675",
      //   js: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/elementui.js?v=149349207657934288611744851797"
      // };
      const assets = {
        jq: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/jquery-3.7.1.min.js",
        js: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/bootstrap-datepicker.min.js",
        bootstrap_css: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/bootstrap.min.css",
        css: "//apexhome-us.myshopify.com/cdn/shop/t/1/assets/bootstrap-datepicker3.min.css",
      };
      this.loadScript(assets.jq).then(() => {
        Promise.all([this.loadScript(assets.js), this.loadCss(assets.bootstrap_css), this.loadCss(assets.css)]).then(() => {
          this.isUILoaded = true;
          this.initDatePicker();
        });
      });
    }

    initDatePicker() {
      // 初始化日期为未来7天
      const [start, end] = this.getDateRange();
      $(".input-daterange .form-control")[0].value = start;
      $(".input-daterange .form-control")[1].value = end;
      $(".input-daterange")
        .datepicker({})
        .on("changeDate", () => {
          const [start, end] = this.getSelectedDate();
          console.log("changeDate", start, end);
        });
    }

    getSelectedDate() {
      return [$(".input-daterange .form-control")[0]?.value, $(".input-daterange .form-control")[1]?.value];
    }

    getDateRange() {
      const start = new Date();
      const end = new Date();
      end.setDate(start.getDate() + 7);
      return [this.formatDate(start), this.formatDate(end)];
    }

    formatDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${month}/${day}/${year}`;
    }

    loadCss(url) {
      return new Promise((resolve, reject) => {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = url;
        link.onload = resolve;
        link.onerror = reject;
        document.head.appendChild(link);
      });
    }

    loadScript(url) {
      return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = url;
        script.onload = resolve;
        script.onerror = reject;
        document.body.appendChild(script);
      });
    }

    show() {
      this.classList.remove("hidden");
      document.body.classList.add("overflow-hidden");
    }

    hide() {
      this.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
    }
  }
);

customElements.define(
  "contact-handle",
  class extends HTMLElement {
    constructor() {
      super();
      this.order = this.getAttribute("type") == "order";
    }
    connectedCallback() {
      this.addEventListener("click", () => {
        document.dispatchEvent(
          new CustomEvent("show:contact", {
            detail: this.order ? this.getOrderData() : null,
          })
        );
      });
    }
    getOrderData() {
      return document.querySelector("scroll-view")?.getOrderData() || null;
    }
  }
);

customElements.define(
  "location-switch",
  class extends HTMLElement {
    constructor() {
      super();
      this.keywords = "";
      this.city = "San Jose";
    }

    getUserCurrentCity() {
      try {
        fetch("https://ipinfo.io?token=37f3b0a7435e58")
          .then((response) => response.json())
          .then((data) => {
            if (localStorage.getItem("__city")) return;
            if (data?.city) {
              if (this.querySelector(`[data-city='${data.city}']`)) this.choose(data.city);
              else this.choose("San Jose");
            } else {
              this.choose("San Jose");
            }
          });
      } catch (e) {
        console.log(e);
        if (localStorage.getItem("__city")) return;
        this.choose("San Jose");
      }
    }

    connectedCallback() {
      this.current = this.querySelector("[data-current]");
      this.updateCurrent();
      this.updateMenuLink();
      this.current.addEventListener("click", () => {
        this.hasAttribute("open") ? this.hide() : this.show();
      });

      this.options = this.querySelectorAll("[data-option]");
      const link = new URL(location.href);
      this.options.forEach((option) => {
        option.addEventListener("click", () => {
          this.choose(option.dataset.city);
          this.hide();
          if (location.href.includes("/pages/order")) {
            setTimeout(() => {
              location.href = `${option.dataset.href}?city=${option.dataset.city}`;
            }, 16);
          } else {
            link.searchParams.set("city", option.dataset.city);
            setTimeout(() => {
              location.href = link.href;
            }, 16);
          }
        });
      });

      this.result = this.querySelector("[data-result]");
      this.input = this.querySelector("[data-search]");
      this.input.addEventListener("input", debounce(this.onKeyUp.bind(this), 500));

      this.overlay = this.querySelector("[data-overlay]");
      this.overlay.addEventListener("click", () => {
        this.hide();
      });
    }

    updateCurrent() {
      const url = new URL(location.href);
      if (url.searchParams.get("city")) {
        this.city = url.searchParams.get("city");
      } else if (localStorage.getItem("__city")) {
        this.city = localStorage.getItem("__city");
      } else {
        this.getUserCurrentCity();
      }
      this.current.innerText = this.city;
    }

    choose(city) {
      this.city = city;
      // this.current.innerText = city;
      localStorage.setItem("__city", city);
      this.updateMenuLink();
    }

    updateMenuLink() {
      document.querySelectorAll("a.list-menu__item, [data-location-link]").forEach((e) => {
        const link = new URL(location.origin + e.getAttribute("href"));
        link.searchParams.set("city", this.city);
        e.setAttribute("href", link.pathname + link.search);
      });
    }

    onKeyUp(event) {
      this.keywords = event.target.value;
      this.search();
    }

    search() {
      let count = 0;
      this.options.forEach((option) => {
        if (option.innerText.toLowerCase().includes(this.keywords.toLowerCase())) {
          option.style.display = "block";
          count++;
        } else {
          option.style.display = "none";
        }
      });
      this.showResult(count);
    }

    showResult(count) {
      if (this.keywords == "") {
        this.result.style.display = "none";
        return;
      }
      if (count > 0) {
        this.result.style.display = "block";
        this.result.innerHTML = `Found ${count} results:`;
      } else {
        this.result.style.display = "block";
        this.result.innerHTML = "<div class='search-empty'>No results found.</div>";
      }
    }

    clear() {
      this.keywords = "";
      this.input.value = "";
      this.search();
    }

    show() {
      this.setAttribute("open", "");
    }

    hide() {
      this.removeAttribute("open");
      this.clear();
    }
  }
);

customElements.define(
  "order-link",
  class extends HTMLElement {
    constructor() {
      super();
      this.city = localStorage.getItem("__city") || "San Jose";
    }

    connectedCallback() {
      this.addEventListener("click", () => {
        location.href = document.querySelector(`location-switch [data-city='${this.city}']`)?.dataset?.href || "/pages/order?city=San Jose";
      });
    }
  }
);
